import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2, Building2, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { isValidCnpj, isValidCpf, maskCnpj, maskCpf, onlyDigits } from "@/lib/format";

const TYPES = {
  PJ: { label: "CNPJ", eyebrow: "Atendimento empresarial", desc: "Informe o CNPJ da empresa para iniciar o atendimento.", placeholder: "00.000.000/0000-00", mask: maskCnpj, valid: isValidCnpj, icon: Building2 },
  PF: { label: "CPF", eyebrow: "Atendimento pessoa física", desc: "Informe o seu CPF para iniciar o atendimento.", placeholder: "000.000.000-00", mask: maskCpf, valid: isValidCpf, icon: User },
};

const Title = () => (
  <h1 className="font-display text-4xl leading-[1.1] text-slate-50 sm:text-5xl" data-testid="cnpj-title">
    Consulta de Pontuação <span className="gold-text">Rewards</span>
  </h1>
);

const TypeChoice = ({ onPick }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-xl" data-testid="person-type-step">
    <p className="eyebrow mb-5">Atendimento Rewards</p>
    <Title />
    <p className="mt-5 text-base text-slate-300 md:text-lg">Para começar, selecione o tipo de cliente.</p>
    <div className="mt-10 grid gap-5 sm:grid-cols-2">
      {[["PF", "Pessoa Física", "Consulta pelo CPF", User], ["PJ", "Pessoa Jurídica", "Consulta pelo CNPJ da empresa", Building2]].map(([k, t, s, Icon]) => (
        <button key={k} onClick={() => onPick(k)} className="option-card group text-left" data-testid={`person-type-${k}`}>
          <Icon className="h-7 w-7" style={{ color: "var(--brand)" }} />
          <p className="mt-5 font-display text-xl text-slate-50">{t}</p>
          <p className="mt-2 text-sm text-slate-400">{s}</p>
          <ArrowRight className="mt-5 h-4 w-4 transition-transform group-hover:translate-x-1" style={{ color: "var(--brand)" }} />
        </button>
      ))}
    </div>
  </motion.div>
);

export const CnpjStep = ({ onSubmit, busy, notice, initialTipo }) => {
  const [tipo, setTipo] = useState(initialTipo || null);
  const [doc, setDoc] = useState("");
  const [error, setError] = useState(notice || "");
  if (!tipo) return <TypeChoice onPick={setTipo} />;
  const t = TYPES[tipo];

  const submit = (e) => {
    e.preventDefault();
    if (!t.valid(doc)) { setError(`${t.label} inválido. Verifique os dígitos informados.`); return; }
    setError("");
    onSubmit(onlyDigits(doc), tipo, e.currentTarget.website?.value || "");
  };

  return (
    <motion.div key={tipo} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-xl">
      <button onClick={() => { setTipo(null); setDoc(""); setError(""); }} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200" data-testid="person-type-back">
        <ArrowLeft className="h-4 w-4" /> Alterar tipo de cliente
      </button>
      <p className="eyebrow mb-5" data-testid="cnpj-eyebrow">{t.eyebrow}</p>
      <Title />
      <p className="mt-5 text-base text-slate-300 md:text-lg" data-testid="cnpj-description">{t.desc}</p>
      <form onSubmit={submit} className="glass-card mt-10 p-6 sm:p-8" data-testid="cnpj-form" noValidate>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid="hp-field" />
        <label htmlFor="doc" className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-400" data-testid="doc-label">{t.label}</label>
        <div className="relative">
          <t.icon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <Input id="doc" inputMode="numeric" autoComplete="off" placeholder={t.placeholder} value={doc}
            onChange={(e) => { setDoc(t.mask(e.target.value)); setError(""); }}
            className="h-14 border-white/10 bg-black/30 pl-12 font-mono-x text-lg tracking-wider text-slate-50 placeholder:text-slate-600 focus-visible:ring-[var(--brand)]"
            data-testid="cnpj-input" aria-invalid={!!error} />
        </div>
        {error && <p className="mt-2 text-sm text-red-400" data-testid="cnpj-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-gold mt-6 h-14 w-full" data-testid="cnpj-submit-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Consultar pontuação <ArrowRight className="h-5 w-5" /></>}
        </button>
        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Ao continuar, o {t.label} será registrado para identificação do atendimento, conforme nossa política de privacidade.
        </p>
      </form>
    </motion.div>
  );
};
